"use client"

import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { ArrowLeft, AlertCircle, CheckCircle, Ticket } from "lucide-react"
import CheckoutPix from "../components/CheckoutPix"
import CheckoutCartao from "../components/CheckoutCartao"
import CheckoutInfinitePay from "../components/CheckoutInfinitePay"
import CheckoutBoleto from "../components/CheckoutBoleto"
import axios from "axios"
import "../styles/compra.css"
import "../styles/pagamento.css"
import "../styles/CupomDesconto.css"
import pixIcon from "../assets/pix-icon.png"
import cartaoIcon from "../assets/card-icon.png"
import boletoIcon from "../assets/boleto-icon.png"
import CheckoutPixAppmax from "../components/CheckoutPixAppmax"
import PixManual from "../components/PixManual"
import CheckoutPixAsaas from "../components/CheckoutPixAsaas"
import CartaoManual from "../components/CartaoManual"
import CheckoutPixPagarme from "../components/CheckoutPixPagarme"
import CheckoutBoletoAsaas from "../components/CheckoutBoletoAsaas"
import CheckoutBoletoParceladoAsaas from "../components/CheckoutBoletoParceladoAsaas"

const Pagamento = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const locationState = location.state || {}
  const {
    pedidoId,
    nome = "Não informado",
    categoriaSelecionada = "Não especificada",
    totalPedido = 0,
    nomeEvento,
    eventoid,
    senhasEscolhidas = {},
    // ✅ O ResumoPedido agora passa o objeto evento completo
    // Isso evita depender do populate do backend para ler boletoParcelado
    evento: eventoDoState = null,
  } = locationState

  const [metodoPagamento, setMetodoPagamento] = useState("")
  const [taxas, setTaxas] = useState({})
  const [taxaAplicada, setTaxaAplicada] = useState(0)
  const [totalComTaxa, setTotalComTaxa] = useState(Number.parseFloat(totalPedido))
  const [statusPagamento, setStatusPagamento] = useState("")
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState("")
  const [activeProviders, setActiveProviders] = useState({ activePixProvider: 'mercado_pago' })
  const [boletoPermitido, setBoletoPermitido] = useState(true)
  const [mensagemBoletoBloqueado, setMensagemBoletoBloqueado] = useState("")
  const [avisoBoleto, setAvisoBoleto] = useState("")
  const [cupomVale, setCupomVale] = useState("")
  const [aplicandoCupom, setAplicandoCupom] = useState(false)
  const [erroCupom, setErroCupom] = useState("")
  const [pedidoTotalAtual, setPedidoTotalAtual] = useState(Number.parseFloat(totalPedido) || 0)
  const [codigoDesconto, setCodigoDesconto] = useState("")
  const [descontoAplicado, setDescontoAplicado] = useState(null)
  const [aplicandoDesconto, setAplicandoDesconto] = useState(false)
  const [erroDesconto, setErroDesconto] = useState("")
  const [mostrarCampoDesconto, setMostrarCampoDesconto] = useState(false)

  // ── Dados do pedido vindos da API (para pegar whatsapp do usuário etc.) ──
  const [pedidoCompleto, setPedidoCompleto] = useState(null)

  // ── O evento pode vir do state (rápido) ou do pedido via API (fallback) ──
  // Prioridade: eventoDoState > pedidoCompleto.evento
  const eventoAtual = eventoDoState || pedidoCompleto?.evento || null

  // Buscar taxas
  useEffect(() => {
    buscarTaxas()
  }, [])

  // Buscar provedor ativo
  useEffect(() => {
    ;(async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/gateways/active`)
        setActiveProviders(res.data || { activePixProvider: 'mercado_pago' })
      } catch (e) {
        console.warn('Falha ao carregar provedores ativos, usando defaults.', e)
      }
    })()
  }, [])

  // Buscar pedido via API — para pegar whatsapp do usuário e verificar prazo boleto
  useEffect(() => {
    const buscarPedido = async () => {
      if (!pedidoId) return
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/pedidos/${pedidoId}`)
        const pedido = res.data
        setPedidoCompleto(pedido)
        const totalAtual = Number(pedido?.total ?? totalPedido) || 0
        setPedidoTotalAtual(totalAtual)
        setTotalComTaxa(totalAtual.toFixed(2))
        if (Number(pedido?.desconto?.valor) > 0) {
          setDescontoAplicado(pedido.desconto)
          setCodigoDesconto(pedido.desconto.codigo || "")
        }

        // Usa o evento da API se o state não trouxe (fallback)
        const ev = eventoDoState || pedido?.evento

        // Verifica prazo de boleto (24h antes do fim das vendas)
        if (!ev?.dataFim) {
          setBoletoPermitido(true)
          setMensagemBoletoBloqueado("")
          return
        }

        const dataFimVendas = new Date(ev.dataFim)
        dataFimVendas.setHours(23, 59, 59, 999)
        const diffHoras = (dataFimVendas.getTime() - Date.now()) / (1000 * 60 * 60)

        if (diffHoras < 24) {
          setBoletoPermitido(false)
          setMensagemBoletoBloqueado("Boleto indisponível devido à proximidade do encerramento das vendas.")
        } else {
          setBoletoPermitido(true)
          setMensagemBoletoBloqueado("")
        }
      } catch (err) {
        console.error("Erro ao buscar pedido:", err)
        setBoletoPermitido(false)
      }
    }
    buscarPedido()
  }, [pedidoId])

  useEffect(() => {
    const audio = new Audio("/audios/pagamento.mp3")
    audio.play().catch(e => console.error("Erro ao reproduzir áudio:", e))
    return () => { audio.pause(); audio.currentTime = 0 }
  }, [])

  const buscarTaxas = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/taxas`)
      const taxasArray = res.data
      setTaxas({
        pix:    taxasArray.find((t) => t.metodo === "pix"),
        cartao: taxasArray.find((t) => t.metodo === "cartao_credito"),
        boleto: taxasArray.find((t) => t.metodo === "boleto"),
      })
    } catch (err) {
      console.error("Erro ao buscar taxas", err)
      setErro("Não foi possível carregar as taxas de pagamento.")
    } finally {
      setLoading(false)
    }
  }

  const calcularTotalComTaxa = (metodo, valorBase = pedidoTotalAtual) => {
    const total = Number(valorBase || 0)
    let taxa = 0
    if (metodo === "pix" && taxas.pix) {
      taxa = taxas.pix.tipo === "porcentagem" ? total * (taxas.pix.valor / 100) : taxas.pix.valor
    }
    if (metodo === "cartao_credito" && taxas.cartao) {
      taxa = taxas.cartao.tipo === "porcentagem" ? total * (taxas.cartao.valor / 100) : taxas.cartao.valor
    }
    if (metodo === "boleto" && taxas.boleto) {
      taxa = taxas.boleto.tipo === "fixo" ? taxas.boleto.valor : total * (taxas.boleto.valor / 100)
    }
    setTaxaAplicada(taxa.toFixed(2))
    setTotalComTaxa((total + taxa).toFixed(2))
  }

  const aplicarCupomDesconto = async (e) => {
    e?.preventDefault?.()
    const codigo = String(codigoDesconto || "").trim()
    if (!codigo) return setErroDesconto("Digite o código do cupom.")
    setAplicandoDesconto(true)
    setErroDesconto("")
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/pedidos/${pedidoId}/aplicar-cupom-desconto`, { codigo })
      const pedido = data?.pedido
      if (!pedido || !Number.isFinite(Number(pedido.total))) throw new Error("Não foi possível confirmar o desconto.")
      const total = Number(pedido.total)
      setPedidoTotalAtual(total)
      setDescontoAplicado(pedido.desconto)
      setCodigoDesconto(pedido.desconto?.codigo || codigo.toUpperCase())
      if (metodoPagamento) calcularTotalComTaxa(metodoPagamento, total)
      else setTotalComTaxa(total.toFixed(2))
    } catch (err) {
      setErroDesconto(err?.response?.data?.mensagem || err?.message || "Cupom inválido ou indisponível.")
    } finally {
      setAplicandoDesconto(false)
    }
  }

  const removerCupomDesconto = async () => {
    setAplicandoDesconto(true)
    setErroDesconto("")
    try {
      const { data } = await axios.delete(`${process.env.REACT_APP_API_URL}/pedidos/${pedidoId}/cupom-desconto`)
      const total = Number(data?.pedido?.total ?? pedidoCompleto?.valorBruto ?? totalPedido)
      setPedidoTotalAtual(total)
      setDescontoAplicado(null)
      setCodigoDesconto("")
      setTotalComTaxa(total.toFixed(2))
    } catch (err) {
      setErroDesconto(err?.response?.data?.mensagem || "Não foi possível remover o cupom.")
    } finally {
      setAplicandoDesconto(false)
    }
  }

  const handleMetodoClick = async (metodo) => {
    sessionStorage.setItem("metodoSelecionado", metodo)
    setErro("")
    setMetodoPagamento("")

    // O cartão é manual: não existe um gateway que grave o método ao abrir o
    // componente. Por isso registramos a escolha imediatamente no pedido.
    // O backend também inicia aqui o prazo de 2 horas para pagamento.
    if (metodo === "cartao_credito" && pedidoId) {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/pedidos/${pedidoId}/metodo-pagamento`,
          { metodo: "cartao_credito" }
        )
      } catch (err) {
        console.error("Erro ao registrar cartão no pedido:", err)
        setErro(
          err?.response?.data?.mensagem ||
          "Não foi possível registrar o cartão como forma de pagamento. Tente novamente."
        )
        return
      }
    }

    setMetodoPagamento(metodo)
    calcularTotalComTaxa(metodo)
  }

  const aplicarCupomVale = async (e) => {
    e?.preventDefault?.()
    const codigo = String(cupomVale || "").trim()
    if (!codigo) {
      setErroCupom("Digite o código do cupom.")
      return
    }

    setAplicandoCupom(true)
    setErroCupom("")
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/pedidos/${pedidoId}/pagar-com-cupom-vale`,
        { codigo }
      )
      const pedidoAtualizado = res?.data?.pedido
      if (!pedidoAtualizado || pedidoAtualizado.statusPagamento !== "pago") {
        throw new Error("A API não confirmou o pagamento como vale.")
      }
      setStatusPagamento("pago")
      navigate(`/pedido/concluido/${pedidoAtualizado._id}`)
    } catch (err) {
      console.error("Erro ao aplicar cupom de vale:", err)
      setErroCupom(
        err?.response?.data?.mensagem ||
        err?.message ||
        "Cupom inválido ou indisponível."
      )
    } finally {
      setAplicandoCupom(false)
    }
  }

  // Polling status do pedido (a cada 3 segundos)
  useEffect(() => {
    if (!pedidoId) return
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/pedidos/${pedidoId}`)
        if (res.data.statusPagamento === "pago") {
          clearInterval(interval)
          navigate(`/pedido/concluido/${res.data._id}`)
        }
        setStatusPagamento(res.data.statusPagamento)
      } catch (err) {
        console.error("Erro ao verificar status do pedido", err)
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [pedidoId, navigate])

  // ── Verifica se o evento permite boleto parcelado ─────────────────────────
  // eventoAtual já é o objeto completo com boletoParcelado (vindo do state)
  const eventoPermiteParcelado =
    activeProviders?.activeBoletoProvider === "asaas" &&
    eventoAtual?.boletoParcelado?.ativo === true &&
    (eventoAtual?.boletoParcelado?.parcelasDisponiveis || []).length > 0

  if (!pedidoId || pedidoId === "undefined") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="pagamento-container fluxo-compra-simples pagamento-simples"
      >
        <h1>Não foi possível abrir</h1>
        <div className="pagamento-erro">
          <AlertCircle size={24} />
          <p>ID do pedido não encontrado. Volte e tente novamente.</p>
        </div>
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
          Voltar
        </motion.button>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="pagamento-container fluxo-compra-simples pagamento-simples"
    >
      <header className="flow-simple-header payment-header">
        <button type="button" className="simple-header-back" onClick={() => navigate(-1)} aria-label="Voltar">
          <ArrowLeft size={22} />
        </button>
        <div>
          <span className="flow-step-badge">Pagamento</span>
          <h1>Como quer pagar?</h1>
        </div>
      </header>

      {erro ? (
        <div className="pagamento-erro">
          <AlertCircle size={20} />
          <p>{erro}</p>
        </div>
      ) : loading ? (
        <div className="pagamento-loading">
          <div className="loading-spinner"></div>
          <p>Carregando informações de pagamento...</p>
        </div>
      ) : (
        <>
          <div className="cupom-desconto-checkout">
            {descontoAplicado ? (
              <div className="cupom-desconto-checkout__applied">
                <span><Ticket size={15} /> Cupom <strong>{descontoAplicado.codigo}</strong> aplicado · desconto de <strong>R$ {Number(descontoAplicado.valor).toFixed(2)}</strong></span>
                {!metodoPagamento && <button type="button" className="cupom-desconto-checkout__remove" onClick={removerCupomDesconto} disabled={aplicandoDesconto}>Remover</button>}
              </div>
            ) : (
              <>
                <button type="button" className="cupom-desconto-checkout__toggle" onClick={() => setMostrarCampoDesconto((aberto) => !aberto)} aria-expanded={mostrarCampoDesconto}>
                  <Ticket size={15} /> <span>Tem um cupom de desconto?</span><strong>{mostrarCampoDesconto ? "Fechar" : "Adicionar"}</strong>
                </button>
                {mostrarCampoDesconto && (
                  <form className="cupom-desconto-checkout__form" onSubmit={aplicarCupomDesconto}>
                    <input className="cupom-desconto-checkout__input" aria-label="Cupom de desconto" value={codigoDesconto} onChange={(e) => { setCodigoDesconto(e.target.value.toUpperCase().replace(/\s+/g, "")); setErroDesconto("") }} placeholder="Código do cupom" maxLength={40} disabled={aplicandoDesconto || Boolean(metodoPagamento)} />
                    <button className="cupom-desconto-checkout__button" type="submit" disabled={aplicandoDesconto || Boolean(metodoPagamento) || !codigoDesconto.trim()}>{aplicandoDesconto ? "Validando..." : "Aplicar"}</button>
                  </form>
                )}
              </>
            )}
            {erroDesconto && <div className="cupom-desconto-checkout__error" role="alert">{erroDesconto}</div>}
          </div>

          <div className="pagamento-resumo simple-payment-total">
            {descontoAplicado && <small className="cupom-desconto-checkout__summary">Valor original: R$ {Number(descontoAplicado.valorBruto || (pedidoTotalAtual + Number(descontoAplicado.valor))).toFixed(2)} · Desconto: − R$ {Number(descontoAplicado.valor).toFixed(2)}</small>}
            <span>TOTAL</span>
            <strong>R$ {Number.parseFloat(totalComTaxa).toFixed(2)}</strong>
            {metodoPagamento && Number(taxaAplicada) > 0 && (
              <small>Taxa de R$ {taxaAplicada} incluída</small>
            )}
          </div>

          <h2 className="simple-payment-question">Escolha uma opção</h2>
          <div className="botoes-pagamento">
            <motion.div
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.95 }}
              className={`botao-metodo ${metodoPagamento === "pix" ? "ativo" : ""}`}
              onClick={() => handleMetodoClick("pix")}
            >
              <img src={pixIcon || "/placeholder.svg"} alt="Pix" />
              <span>PIX</span><small>Mais rápido</small>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.95 }}
              className={`botao-metodo ${metodoPagamento === "cartao_credito" ? "ativo" : ""}`}
              onClick={() => handleMetodoClick("cartao_credito")}
            >
              <img src={cartaoIcon || "/placeholder.svg"} alt="Cartão" />
              <span>CARTÃO</span><small>Crédito</small>
            </motion.div>

            <motion.div
              whileHover={boletoPermitido ? { y: -5 } : {}}
              whileTap={boletoPermitido ? { scale: 0.95 } : {}}
              className={`botao-metodo ${metodoPagamento === "boleto" ? "ativo" : ""} ${!boletoPermitido ? "bloqueado" : ""}`}
              onClick={() => {
                if (!boletoPermitido) {
                  setAvisoBoleto(mensagemBoletoBloqueado)
                  return
                }
                handleMetodoClick("boleto")
              }}
            >
              <img src={boletoIcon} alt="Boleto" />
              <span>{eventoPermiteParcelado ? "BOLETO PARCELADO" : "BOLETO"}</span><small>{boletoPermitido ? "Gerar boleto" : "Indisponível"}</small>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.95 }}
              className={`botao-metodo botao-metodo-vale ${metodoPagamento === "vale" ? "ativo" : ""}`}
              onClick={() => handleMetodoClick("vale")}
            >
              <Ticket className="vale-payment-icon" size={42} aria-hidden="true" />
              <span>VALE</span><small>Usar cupom</small>
            </motion.div>

            {avisoBoleto && (
              <div className="pagamento-aviso">
                <AlertCircle size={18} />
                <p>{avisoBoleto}</p>
              </div>
            )}
          </div>

          <AnimatePresence mode="wait">

            {/* ── PIX ──────────────────────────────────────────────────────── */}
            {metodoPagamento === "pix" && (
              <motion.div
                key="pix"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activeProviders.activePixProvider === 'mercado_pago' && (
                  <CheckoutPix pedidoId={pedidoId} total={Number.parseFloat(totalComTaxa)} nomeVaqueiro={nome} eventoid={eventoid} nomeEvento={nomeEvento} />
                )}
                {activeProviders.activePixProvider === 'appmax' && (
                  <CheckoutPixAppmax pedidoId={pedidoId} total={Number.parseFloat(totalComTaxa)} nomeVaqueiro={nome} eventoid={eventoid} nomeEvento={nomeEvento} valorTaxa={Number.parseFloat(taxaAplicada)} totalComTaxa={Number.parseFloat(totalComTaxa)} categoria={categoriaSelecionada} />
                )}
                {activeProviders.activePixProvider === 'asaas' && (
                  <CheckoutPixAsaas pedidoId={pedidoId} total={Number.parseFloat(totalComTaxa)} nomeVaqueiro={nome} email={""} cpfCnpj={""} phone={""} eventoid={eventoid} nomeEvento={nomeEvento} />
                )}
                {activeProviders.activePixProvider === 'pagarme' && (
                  <CheckoutPixPagarme pedidoId={pedidoId} total={Number.parseFloat(totalComTaxa)} nomeVaqueiro={nome} email={""} cpfCnpj={""} phone={""} eventoid={eventoid} nomeEvento={nomeEvento} />
                )}
                {activeProviders.activePixProvider === 'pix_manual' && (
                  <PixManual valor={Number.parseFloat(totalComTaxa)} senhas={Object.values(senhasEscolhidas)} nomeVaqueiro={nome} />
                )}
              </motion.div>
            )}

            {/* ── CARTÃO ───────────────────────────────────────────────────── */}
            {metodoPagamento === "cartao_credito" && (
              <motion.div
                key="cartao"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activeProviders.activeCardProvider === "infinitepay" ? (
                  <CheckoutInfinitePay pedidoId={pedidoId} total={Number.parseFloat(totalComTaxa)} />
                ) : activeProviders.activeCardProvider === "mercado_pago" ? (
                  <CheckoutCartao pedidoId={pedidoId} total={Number.parseFloat(totalComTaxa)} valorTaxa={Number.parseFloat(taxaAplicada)} totalPedido={Number.parseFloat(pedidoTotalAtual)} />
                ) : (
                  <CartaoManual pedidoId={pedidoId} valor={Number.parseFloat(totalComTaxa)} nomeVaqueiro={nome} nomeEvento={nomeEvento} />
                )}
              </motion.div>
            )}

            {/* ── BOLETO ───────────────────────────────────────────────────── */}
            {metodoPagamento === "boleto" && (
              <motion.div
                key="boleto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {eventoPermiteParcelado ? (
                  // ✅ Evento com parcelamento ativo → checkout parcelado
                  <CheckoutBoletoParceladoAsaas
                    pedido={{
                      _id: pedidoId,
                      // Passa o total SEM taxa de sistema — a taxa de parcelamento
                      // é somada por parcela dentro do CheckoutBoletoParceladoAsaas
                      total: Number.parseFloat(pedidoTotalAtual),
                      usuario: {
                        whatsapp: pedidoCompleto?.usuario?.whatsapp || "",
                      },
                      // ✅ Usa eventoAtual (do state, sem depender do populate da API)
                      evento: eventoAtual,
                    }}
                    voltar={() => setMetodoPagamento("")}
                  />
                ) : activeProviders?.activeBoletoProvider === "asaas" ? (
                  // Sem parcelamento, gateway Asaas → boleto avulso
                  <CheckoutBoletoAsaas
                    pedido={{
                      _id: pedidoId,
                      usuario: { email: "", whatsapp: "" },
                      total: Number.parseFloat(totalComTaxa),
                    }}
                    voltar={() => setMetodoPagamento("")}
                  />
                ) : (
                  // Fallback: boleto Mercado Pago
                  <CheckoutBoleto
                    pedido={{
                      _id: pedidoId,
                      usuario: { email: "" },
                      total: Number.parseFloat(totalComTaxa),
                    }}
                    voltar={() => setMetodoPagamento("")}
                  />
                )}
              </motion.div>
            )}

          </AnimatePresence>

          {metodoPagamento === "vale" && (
            <motion.form
              key="vale"
              className="cupom-vale-checkout"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              onSubmit={aplicarCupomVale}
            >
              <div className="cupom-vale-title">
                <Ticket size={20} />
                <div>
                  <strong>Pagamento com vale</strong>
                  <small>Digite o cupom fornecido pelo organizador do evento.</small>
                </div>
              </div>

              <label htmlFor="cupom-vale">Cupom</label>
              <div className="cupom-vale-row">
                <input
                  id="cupom-vale"
                  type="text"
                  value={cupomVale}
                  onChange={(e) => {
                    setCupomVale(e.target.value.toUpperCase().replace(/\s+/g, ""))
                    setErroCupom("")
                  }}
                  placeholder="Ex.: VAQUE013"
                  autoComplete="off"
                  maxLength={40}
                  disabled={aplicandoCupom}
                />
                <button type="submit" disabled={aplicandoCupom || !cupomVale.trim()}>
                  {aplicandoCupom ? "Validando..." : "Aplicar cupom"}
                </button>
              </div>

              {erroCupom && (
                <div className="cupom-vale-erro">
                  <AlertCircle size={17} />
                  <span>{erroCupom}</span>
                </div>
              )}

              <p className="cupom-vale-nota">
                Ao validar, este pedido será confirmado como pago e registrado automaticamente em Vales.
              </p>
            </motion.form>
          )}

          {statusPagamento === "pago" && (
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="status-pago">
              <CheckCircle size={20} />
              Pagamento confirmado!
            </motion.div>
          )}
        </>
      )}
    </motion.div>
  )
}

export default Pagamento
